import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronRight } from "lucide-react";
import { Screen, ScrollArea } from "@/components/jk/ui";
import { BottomNav, SectionLabel, FullScreenLoader } from "@/components/jk/kit";
import { formatDate, formatTime, weekdayShort } from "@/lib/format";
import AvailabilityManager from "@/components/jk/AvailabilityManager";
import api from "@/lib/api";

const Calendar = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("schedule");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/calendar").then(({ data }) => setBookings(data.bookings || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const grouped = bookings.reduce((acc, b) => {
    (acc[b.booking_date] = acc[b.booking_date] || []).push(b);
    return acc;
  }, {});

  return (
    <Screen testid="calendar-screen">
      <header className="shrink-0 px-6 pb-3 pt-8">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#111111]">Calendar</h1>
        <p className="mt-1 text-[13px] text-[#666666]">Your schedule &amp; availability</p>
        <div className="mt-4 flex gap-1 rounded-full bg-[#f2f0ec] p-1" data-testid="calendar-tabs">
          {[["schedule", "Schedule"], ["availability", "Availability"]].map(([k, label]) => (
            <button key={k} data-testid={`cal-tab-${k}`} onClick={() => setTab(k)}
              className={`flex-1 rounded-full py-2 text-[13px] font-semibold transition-colors ${tab === k ? "bg-[#111111] text-white" : "text-[#666666]"}`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <ScrollArea className="px-6 pt-4">
        {tab === "availability" ? (
          <AvailabilityManager />
        ) : loading ? (
          <div className="pt-20"><FullScreenLoader /></div>
        ) : bookings.length === 0 ? (
          <div className="flex flex-col items-center pt-24 text-center" data-testid="calendar-empty">
            <CalendarDays size={28} strokeWidth={1.5} className="text-[#cfccc6]" />
            <p className="mt-3 text-[15px] font-medium text-[#111111]">No upcoming consultations</p>
            <p className="mt-1 text-[13px] text-[#666666]">New bookings will appear here.</p>
          </div>
        ) : (
          <div className="space-y-6 pb-6" data-testid="calendar-list">
            {Object.entries(grouped).map(([date, items]) => (
              <div key={date}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-[13px] font-bold text-[#111111]">{weekdayShort(date)}</span>
                  <SectionLabel>{formatDate(date)}</SectionLabel>
                </div>
                <div className="space-y-2.5">
                  {items.map((b) => (
                    <button key={b.id} onClick={() => navigate(`/brief/${b.id}`)} className="flex w-full items-center gap-3 rounded-[14px] border border-[#e9e6e1] bg-white p-3.5 text-left active:scale-[0.99]">
                      <div className="flex w-16 shrink-0 flex-col items-center rounded-[10px] bg-[#f2f0ec] py-1.5">
                        <span className="text-[13px] font-bold text-[#111111]">{formatTime(b.booking_time).split(" ")[0]}</span>
                        <span className="text-[10px] text-[#666666]">{formatTime(b.booking_time).split(" ")[1]}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[14px] font-semibold text-[#111111]">{b.client_name}</p>
                        <p className="truncate text-[12px] text-[#666666]">{b.service_name}</p>
                      </div>
                      <ChevronRight size={18} className="text-[#cfccc6]" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <BottomNav />
    </Screen>
  );
};

export default Calendar;
